import { splitSupportErrorId } from '../../../utils/analytics';

/**
 * Pure decision behind the survey publish-modal pre-select effect (PSTUDIO-2081).
 *
 * Returns the main-solution ids to seed into the modal's selection for the given
 * environment, or `null` when the current selection must be left untouched:
 *   - the bot is not a survey (the main-solution <Select> isn't shown), or
 *   - the user has already edited the selection (their manual choice wins until the
 *     modal is closed and reopened).
 *
 * When seeding, it returns the ids the survey is linked to in `selectedEnv`, or `[]`
 * when there are none / the link data hasn't loaded yet (`surveyLinkedSolutions`
 * undefined — e.g. while loading, or after the machine cleared it on a failed/fresh
 * load). Extracted from the component so this logic is unit-testable without React:
 * pro-studio's vitest environment is `node` with no @testing-library/react.
 */
export interface ComputeSeededBotsArgs {
  isSurvey: boolean;
  userEditedBots: boolean;
  selectedEnv: string;
  surveyLinkedSolutions?: { live: string[]; sandbox: string[] };
}

export const computeSeededBots = ({
  isSurvey,
  userEditedBots,
  selectedEnv,
  surveyLinkedSolutions,
}: ComputeSeededBotsArgs): string[] | null => {
  if (!isSurvey || userEditedBots) {
    return null;
  }

  const linkedForEnv =
    selectedEnv === 'live'
      ? surveyLinkedSolutions?.live
      : surveyLinkedSolutions?.sandbox;

  return linkedForEnv ?? [];
};

/** Minimal shape `computeActiveCoworkers` needs; `SolutionUserPresenceItem`
 * satisfies it structurally, so callers keep their own richer element type. */
export interface CoworkerPresence {
  isActive: boolean;
  userId?: string;
  user?: { id: string };
}

/**
 * The coworkers whose presence drives the publish modal's warning banner
 * ("Other people are working on this solution…", plus the avatars and the
 * "Publish anyway" button label): everyone currently active on the solution
 * except the person opening the modal.
 *
 * PSTUDIO-3597: this decision is deliberately **solution-type agnostic** — a
 * presence row carries no bot type, so a survey solution warns exactly like a
 * main one. Do NOT add an `isSurvey`/`botType` condition here or at the call
 * site; the banner must show for surveys too. Extracted from the component (as
 * `computeSeededBots` above was) so the rule is unit-testable without React:
 * pro-studio's vitest environment is `node` with no @testing-library/react.
 *
 * The id comparison is stringified because presence rows may identify the user
 * through either `userId` or the joined `user.id`, and ids arrive as strings or
 * numbers depending on the source.
 */
export const computeActiveCoworkers = <T extends CoworkerPresence>(
  presences: T[] | undefined,
  currentUserId: string | number | null | undefined
): T[] => {
  const currentId = String(currentUserId ?? '');
  return (presences ?? []).filter(
    (p) => p.isActive && String(p.userId ?? p.user?.id ?? '') !== currentId
  );
};

/**
 * PSTUDIO-3985: does this publish have NO status watcher, so that the modal
 * itself must release the UI state it raised?
 *
 * `onPublish` raises three things the instant the user confirms — the publish
 * lock (`canPublish` reads it), a forced `'viewing'` mode, and a toast with
 * `autoClose: false`. Lifting them again is the job of the router-level
 * `usePublishStatusWatcherForCoworkers`, which is the single owner of publish
 * completion since this ticket removed the modal's own, unscoped watcher.
 *
 * That watcher subscribes to `ctx.draftVersionSolutionData?.id` and early-returns
 * when it is null — and a null draft id is precisely what routes
 * `publishBotVersion` into its `createAndPublishSolutionVersion` branch, which
 * succeeds and hands back a brand-new version id nobody is subscribed to. Keyed
 * on "did the mutation return an id", the release therefore never fired for that
 * path and left the user read-only behind a permanent banner until a reload.
 *
 * The question is deliberately "is anything watching", NOT "does the returned id
 * match the draft". `publishSolutionVersion` runs with `doVersionBump: true`, so
 * the id it returns may differ from the draft the watcher is subscribed to;
 * releasing on a mismatch would tear the banner down while the publish is still
 * in flight.
 */
export const publishCompletionHasNoWatcher = (
  draftSolutionVersionId: string | null | undefined
): boolean => !draftSolutionVersionId;

/** The fallback `publishBotVersion` settles with when a throw carries no message. */
const GENERIC_PUBLISH_ERROR = 'Error publishing solution';

/**
 * What to tell the user about a publish the backend refused, or `null` for the
 * generic "reach out to your point of contact".
 *
 * The backend's own reasons are ones the user can act on — CONFLICT ("being published
 * by another user"), "Version is not a draft", "never deployed to <env>" — and the
 * modal used to discard every one of them. The support-id suffix `throwErrorWithId`
 * appends is dropped: it belongs in a report, not in a toast. The invoke's own
 * catch-all wording says nothing specific, so it maps to `null` too.
 */
export const readPublishFailureReason = (
  errorMessage: string | undefined
): string | null => {
  if (!errorMessage) return null;
  const { message } = splitSupportErrorId(errorMessage);
  const reason = message.trim();
  if (!reason || reason === GENERIC_PUBLISH_ERROR) return null;
  return reason;
};
